import type { PaymentMethod } from "@paisa/types"

export interface NarrationResult {
  paymentMethod: PaymentMethod
  name: string // merchant or person name
  upiId: string | null
  referenceNumber: string | null
  description: string | null // trailing description (e.g., "PAYMENT ON CRED", "UBERRIDE")
}

/**
 * Parse HDFC bank narration string into structured fields.
 *
 * HDFC narration patterns:
 *   UPI:  "UPI-NAME-UPI_ID-IFSC-REF-DESCRIPTION"
 *   NEFT: "NEFT CR-IFSC-COMPANY-BENEFICIARY-REF" or "NEFT DR-..."
 *   ACH:  "ACH D- NAME-REF" or "ACH C- NAME-REF"
 *   FT:   "FT- REF-ACCOUNT - NAME -"
 *   IMPS: "IMPS-REF-NAME-..."
 *   POS:  "POS XXXXXX NAME CITY" or just "NAME/CITY"
 *   ATM:  "ATW-..." or "ATM-..."
 */
export function parseHdfcNarration(narration: string): NarrationResult {
  const trimmed = narration.trim()

  // UPI transactions — most common
  if (trimmed.startsWith("UPI-")) {
    return parseUpi(trimmed)
  }

  // NEFT credit/debit
  if (trimmed.startsWith("NEFT CR-") || trimmed.startsWith("NEFT DR-")) {
    return parseNeft(trimmed)
  }

  // ACH (auto-debit/credit)
  if (trimmed.startsWith("ACH D-") || trimmed.startsWith("ACH C-")) {
    return parseAch(trimmed)
  }

  // Fund transfer (investment returns, clearing corp)
  if (trimmed.startsWith("FT-") || trimmed.startsWith("FT -")) {
    return parseFt(trimmed)
  }

  // IMPS
  if (trimmed.startsWith("IMPS-") || trimmed.startsWith("IMPS/")) {
    return parseImps(trimmed)
  }

  // ATM withdrawal
  if (trimmed.startsWith("ATW-") || trimmed.startsWith("ATM-") || trimmed.startsWith("ATM/")) {
    return {
      paymentMethod: "atm",
      name: "ATM Withdrawal",
      upiId: null,
      referenceNumber: null,
      description: trimmed,
    }
  }

  // POS transactions
  if (trimmed.startsWith("POS ")) {
    return parsePos(trimmed)
  }

  // Fallback — could be POS without prefix, card transactions, etc.
  return parseFallback(trimmed)
}

/** UPI-NAME-UPI_ID-IFSC-REF-DESCRIPTION */
function parseUpi(narration: string): NarrationResult {
  // Remove "UPI-" prefix
  const rest = narration.slice(4)
  const parts = rest.split("-")

  // Minimum: NAME-UPI_ID-IFSC-REF
  if (parts.length < 4) {
    return {
      paymentMethod: "upi",
      name: rest,
      upiId: null,
      referenceNumber: null,
      description: null,
    }
  }

  const name = parts[0].trim()
  const upiId = parts[1]?.trim() || null

  // Reference number is typically the 5th part (index 3 after 0-indexed)
  // but IFSC is at index 2, so ref is at index 3
  const referenceNumber = parts[3]?.trim() || null

  // Everything after ref is description (joined back with dashes)
  const description = parts.length > 4 ? parts.slice(4).join("-").trim() : null

  return {
    paymentMethod: "upi",
    name: cleanName(name),
    upiId,
    referenceNumber,
    description: description || null,
  }
}

/** NEFT CR-IFSC-COMPANY-BENEFICIARY-REF */
function parseNeft(narration: string): NarrationResult {
  const isCredit = narration.startsWith("NEFT CR-")
  const rest = narration.slice(isCredit ? 8 : 8) // "NEFT CR-" or "NEFT DR-" both 8 chars
  const parts = rest.split("-")

  // IFSC-COMPANY-BENEFICIARY-REF
  if (parts.length < 2) {
    return {
      paymentMethod: "neft",
      name: rest,
      upiId: null,
      referenceNumber: null,
      description: null,
    }
  }

  // parts[0] = IFSC, parts[1] = company name, parts[2] = beneficiary, parts[3] = ref
  const companyName = parts[1]?.trim() || parts[0]?.trim() || rest
  const referenceNumber = parts[parts.length - 1]?.trim() || null

  return {
    paymentMethod: "neft",
    name: cleanName(companyName),
    upiId: null,
    referenceNumber,
    description: null,
  }
}

/** ACH D- NAME-REF or ACH C- NAME-REF */
function parseAch(narration: string): NarrationResult {
  // "ACH D- " or "ACH C- " = 7 chars
  const rest = narration.slice(7).trim()
  const parts = rest.split("-")

  const name = parts[0]?.trim() || rest
  const referenceNumber = parts.length > 1 ? parts[parts.length - 1]?.trim() : null

  return {
    paymentMethod: "ach",
    name: cleanName(name),
    upiId: null,
    referenceNumber,
    description: null,
  }
}

/** FT- REF-ACCOUNT - NAME - */
function parseFt(narration: string): NarrationResult {
  // "FT- " or "FT-"
  const rest = narration.replace(/^FT[\s-]+/, "").trim()

  // Pattern: "REF-ACCOUNT - NAME -"
  // Split by " - " (space-dash-space) to get name
  const spaceDashParts = rest.split(" - ")

  if (spaceDashParts.length >= 2) {
    // Name is typically the second-to-last non-empty part
    const namePart = spaceDashParts.find((p, i) => i > 0 && p.trim().length > 0)
    return {
      paymentMethod: "fund_transfer",
      name: cleanName(namePart?.trim() || rest),
      upiId: null,
      referenceNumber: spaceDashParts[0]?.split("-")[0]?.trim() || null,
      description: null,
    }
  }

  return {
    paymentMethod: "fund_transfer",
    name: cleanName(rest),
    upiId: null,
    referenceNumber: null,
    description: null,
  }
}

/** IMPS-REF-NAME-... */
function parseImps(narration: string): NarrationResult {
  const rest = narration.replace(/^IMPS[-/]/, "").trim()
  const parts = rest.split("-")

  const referenceNumber = parts[0]?.trim() || null
  const name = parts.length > 1 ? parts[1]?.trim() : rest

  return {
    paymentMethod: "imps",
    name: cleanName(name || rest),
    upiId: null,
    referenceNumber,
    description: null,
  }
}

/** POS XXXXXX NAME CITY */
function parsePos(narration: string): NarrationResult {
  // "POS " prefix, then sometimes a masked card number, then merchant
  const rest = narration.slice(4).trim()
  // Remove leading card number pattern (6+ digits or Xs)
  const cleaned = rest.replace(/^[0-9X]{4,}\s*/, "").trim()

  return {
    paymentMethod: "pos",
    name: cleanName(cleaned || rest),
    upiId: null,
    referenceNumber: null,
    description: null,
  }
}

/** Fallback for unrecognized patterns */
function parseFallback(narration: string): NarrationResult {
  // Some POS/card transactions are just "MERCHANT_NAME/CITY"
  // e.g., "QOIUXIZWNZQXUX/RAZPBSEINDIACOM" or "TATA DIGITAL PRIVATE LIMUMBAI"
  return {
    paymentMethod: "other",
    name: cleanName(narration),
    upiId: null,
    referenceNumber: null,
    description: null,
  }
}

/** Clean up extracted name — remove corporate suffixes, extra whitespace */
function cleanName(raw: string): string {
  return (
    raw
      // "PRIVATE LIMITED", "PVT LTD", "PVT. LTD." and truncated forms like "PRIVATE LI"
      // HDFC truncates narrations at ~40 chars, so "PRIVATE LIMITED" may appear as "PRIVATE LI"
      .replace(/\s+(PRIVATE|PVT\.?)\s*\w*/gi, "")
      // Standalone "LIMITED" or "LTD" at end
      .replace(/\s+(LIMITED|LTD\.?)(\s|$)/gi, " ")
      // Trailing dots, dashes, spaces
      .replace(/[\s.-]+$/, "")
      .replace(/\s{2,}/g, " ")
      .trim()
  )
}
